// Minimal QR encoder (byte mode, ECC level M, versions 1–6 ≈ up to 106 bytes),
// enough for gt.today links. After Nayuki's reference implementation.

const ECC_PER_BLOCK = [0, 10, 16, 26, 18, 24, 16]
const NUM_BLOCKS = [0, 1, 1, 1, 2, 2, 4]
const MAX_VERSION = 6

function gfMul(x: number, y: number) {
  let z = 0
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d)
    z ^= ((y >>> i) & 1) * x
  }
  return z
}

function rsDivisor(degree: number) {
  const r = Array<number>(degree).fill(0)
  r[degree - 1] = 1
  let root = 1
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      r[j] = gfMul(r[j]!, root)
      if (j + 1 < degree) r[j]! ^= r[j + 1]!
    }
    root = gfMul(root, 2)
  }
  return r
}

function rsRemainder(data: number[], divisor: number[]) {
  const r = divisor.map(() => 0)
  for (const b of data) {
    const factor = b ^ r.shift()!
    r.push(0)
    divisor.forEach((c, i) => (r[i]! ^= gfMul(c, factor)))
  }
  return r
}

const rawModules = (ver: number) =>
  (16 * ver + 128) * ver + 64 - (ver >= 2 ? (25 * (Math.floor(ver / 7) + 2) - 10) * 2 - 55 : 0)

const dataCodewords = (ver: number) =>
  Math.floor(rawModules(ver) / 8) - ECC_PER_BLOCK[ver]! * NUM_BLOCKS[ver]!

function withEcc(data: number[], ver: number) {
  const numBlocks = NUM_BLOCKS[ver]!
  const eccLen = ECC_PER_BLOCK[ver]!
  const raw = Math.floor(rawModules(ver) / 8)
  const numShort = numBlocks - (raw % numBlocks)
  const shortLen = Math.floor(raw / numBlocks)
  const div = rsDivisor(eccLen)
  const blocks: number[][] = []
  for (let i = 0, k = 0; i < numBlocks; i++) {
    const dat = data.slice(k, k + shortLen - eccLen + (i < numShort ? 0 : 1))
    k += dat.length
    const ecc = rsRemainder(dat, div)
    if (i < numShort) dat.push(0)
    blocks.push(dat.concat(ecc))
  }
  const out: number[] = []
  for (let i = 0; i < blocks[0]!.length; i++)
    blocks.forEach((b, j) => {
      if (i !== shortLen - eccLen || j >= numShort) out.push(b[i]!)
    })
  return out
}

const MASKS: ((x: number, y: number) => boolean)[] = [
  (x, y) => (x + y) % 2 === 0,
  (_, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
]

function penalty(m: boolean[][]) {
  const n = m.length
  let p = 0
  let dark = 0
  for (let a = 0; a < n; a++) {
    let runRow = 1
    let runCol = 1
    for (let b = 0; b < n; b++) {
      if (m[a]![b]) dark++
      if (b > 0) {
        runRow = m[a]![b] === m[a]![b - 1] ? runRow + 1 : 1
        runCol = m[b]![a] === m[b - 1]![a] ? runCol + 1 : 1
        if (runRow === 5) p += 3
        else if (runRow > 5) p++
        if (runCol === 5) p += 3
        else if (runCol > 5) p++
      }
      if (a > 0 && b > 0) {
        const c = m[a]![b]
        if (c === m[a - 1]![b] && c === m[a]![b - 1] && c === m[a - 1]![b - 1]) p += 3
      }
    }
  }
  return p + 10 * Math.floor(Math.abs(dark * 20 - n * n * 10) / (n * n))
}

/** QR modules (true = dark), row-major; null if the text is too long. */
export function qrMatrix(text: string): boolean[][] | null {
  const bytes = [...new TextEncoder().encode(text)]
  let ver = 1
  while (ver <= MAX_VERSION && 12 + bytes.length * 8 > dataCodewords(ver) * 8) ver++
  if (ver > MAX_VERSION) return null

  const cap = dataCodewords(ver) * 8
  const bits: number[] = []
  const put = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1)
  }
  put(4, 4)
  put(bytes.length, 8)
  for (const b of bytes) put(b, 8)
  put(0, Math.min(4, cap - bits.length))
  put(0, (8 - (bits.length % 8)) % 8)
  for (let pad = 0xec; bits.length < cap; pad ^= 0xec ^ 0x11) put(pad, 8)
  const data: number[] = []
  for (let i = 0; i < bits.length; i += 8)
    data.push(bits.slice(i, i + 8).reduce((a, b) => (a << 1) | b, 0))
  const codewords = withEcc(data, ver)

  const size = ver * 4 + 17
  const mods = Array.from({ length: size }, () => Array<boolean>(size).fill(false))
  const fn = Array.from({ length: size }, () => Array<boolean>(size).fill(false))
  const set = (x: number, y: number, dark: boolean) => {
    mods[y]![x] = dark
    fn[y]![x] = true
  }

  for (let i = 0; i < size; i++) {
    set(6, i, i % 2 === 0)
    set(i, 6, i % 2 === 0)
  }
  for (const [cx, cy] of [
    [3, 3],
    [size - 4, 3],
    [3, size - 4],
  ] as const)
    for (let dy = -4; dy <= 4; dy++)
      for (let dx = -4; dx <= 4; dx++) {
        const x = cx + dx
        const y = cy + dy
        const d = Math.max(Math.abs(dx), Math.abs(dy))
        if (x >= 0 && x < size && y >= 0 && y < size) set(x, y, d !== 2 && d !== 4)
      }
  if (ver >= 2)
    for (let dy = -2; dy <= 2; dy++)
      for (let dx = -2; dx <= 2; dx++)
        set(size - 7 + dx, size - 7 + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1)

  const drawFormat = (mask: number) => {
    const d = mask // ECC level M = 0b00
    let rem = d
    for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537)
    const f = ((d << 10) | rem) ^ 0x5412
    const bit = (i: number) => ((f >>> i) & 1) !== 0
    for (let i = 0; i <= 5; i++) set(8, i, bit(i))
    set(8, 7, bit(6))
    set(8, 8, bit(7))
    set(7, 8, bit(8))
    for (let i = 9; i < 15; i++) set(14 - i, 8, bit(i))
    for (let i = 0; i < 8; i++) set(size - 1 - i, 8, bit(i))
    for (let i = 8; i < 15; i++) set(8, size - 15 + i, bit(i))
    set(8, size - 8, true)
  }
  drawFormat(0)

  let i = 0
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5
    for (let vert = 0; vert < size; vert++)
      for (let j = 0; j < 2; j++) {
        const x = right - j
        const y = ((right + 1) & 2) === 0 ? size - 1 - vert : vert
        if (!fn[y]![x] && i < codewords.length * 8) {
          mods[y]![x] = ((codewords[i >>> 3]! >>> (7 - (i & 7))) & 1) !== 0
          i++
        }
      }
  }

  let best: boolean[][] = mods
  let bestScore = Infinity
  for (let k = 0; k < MASKS.length; k++) {
    drawFormat(k)
    const m = mods.map((row, y) => row.map((v, x) => (fn[y]![x] ? v : v !== MASKS[k]!(x, y))))
    const score = penalty(m)
    if (score < bestScore) {
      bestScore = score
      best = m
    }
  }
  return best
}

/** SVG path (one unit per module, offset by `margin`) for the dark modules. */
export function qrPath(m: boolean[][], margin = 0) {
  let d = ''
  m.forEach((row, y) =>
    row.forEach((dark, x) => {
      if (dark) d += `M${x + margin} ${y + margin}h1v1h-1z`
    }),
  )
  return d
}
